import { useContext, useEffect, useRef, useState } from "react";
import {
    Avatar,
    Button,
    CircularProgress,
    Dialog,
    Divider,
    Grid,
    IconButton,
    InputAdornment,
    TextField,
    Typography
} from "@mui/material";
import { Box, Container } from "@mui/system";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { Controller, useForm } from "react-hook-form";
import { useSnackbar } from "notistack";
import { useMutation, useQuery } from "react-query";
import { queryClient } from "../../App";
import axios from "../../api/axios";
import AuthContext from "../../context/AuthProvider";
import logo from "../../utils/images/ridePlus.png";
import useOtpResendCooldown from "../../hooks/useOtpResendCooldown";
import { useThemeMode } from "../../context/ThemeContext";
import { useLocation } from "react-router-dom";
import useTenantBranding from "../../hooks/useTenantBranding";

// Forget Password Component
function ForgetPassword(props) {
    const { setOpen } = props

    const { mode } = useThemeMode();
    const { tenantLogo, hideDefaultBranding } = useTenantBranding();
    const location = useLocation();
    const { redirectUri } = useContext(AuthContext);
    const searchParams = new URLSearchParams(location.search);
    const redirectSearchParams = new URLSearchParams(redirectUri?.search || "");
    const clientIdForReset =
        searchParams.get("client_id") ||
        searchParams.get("clientId") ||
        redirectSearchParams.get("client_id") ||
        redirectSearchParams.get("clientId") ||
        location.state?.client_id ||
        location.state?.clientId;

    // USESTATE & USEREF
    const [openDrawer, setOpenDrawer] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [inputType, setInputType] = useState("password");
    const [codeInputType, setCodeInputType] = useState("password");
    const [showCode, setShowCode] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [confirmInputType, setConfirmInputType] = useState("password");
    const [requestOtp, setRequestOtp] = useState(false)

    const password = useRef({});
    const email = useRef({})
    const cooldownHook = useOtpResendCooldown('forgetPassword', email.current);

    // USEFORM HOOK
    const {
        control,
        handleSubmit,
        formState: { errors },
        watch,
    } = useForm({
        mode: "onChange"
    });

    email.current = watch("email", "")
    password.current = watch("password", "");

    // SNACKBAR
    const { enqueueSnackbar } = useSnackbar();

    const { isFetching: otpIsFetching, refetch, isFetched } = useQuery(
        ["otp-forget-password", email.current, clientIdForReset],
        () =>
            axios.get("/resetCode", {
                headers: {
                    "Content-Type": "application/json",
                },
                params: {
                    email: email.current,
                    ...(clientIdForReset && { client_id: clientIdForReset }),
                },
            }),
        {
            refetchOnWindowFocus: false,
            enabled: requestOtp,
            retry: false,
        }
    );
    const { mutate, isLoading, data, error } = useMutation((password) => {
        return axios.post(`/resetPassword`, password);
    });

    // HELPERS
    const handleClick = () => {
        setOpen(false);
    }
    const onSubmit = () => {
        refetch()
    }
    const onReset = (newData) => {
        const toBeSent = { ...newData, email: email.current }
        mutate(toBeSent)
    }

    // USEFFECT
    useEffect(() => {
        if (data) {
            enqueueSnackbar(`Succesfully changed your password.`, {
                variant: "success",
                preventDuplicate: true,
                autoHideDuration: 2000
            });
            queryClient.clear()
            setOpen(false)
        }
    }, [data, enqueueSnackbar, setOpen]);
    useEffect(() => {
        if (isFetched) {
            enqueueSnackbar(`Code sent.`, {
                variant: "success",
                preventDuplicate: true,
                autoHideDuration: 2000
            });
            cooldownHook.startCooldown(); // Start cooldown after successful OTP request
            setOpenDrawer(true)
            queryClient.clear()
            setRequestOtp(false)
        }
    }, [isFetched, enqueueSnackbar, cooldownHook]);
    useEffect(() => {
        if (error) {
            enqueueSnackbar(
                error?.response?.data?.error?.message ||
                error?.message ||
                "Network Error!",
                {
                    variant: "error",
                    preventDuplicate: true,
                    autoHideDuration: 2000
                }
            );
            queryClient.clear()
            setRequestOtp(false)
        }
    }, [enqueueSnackbar, error]);

    // RENDER
    return (
        <Grid
            container
            sx={{
                minHeight: "600px",
                justifyContent: "center",
                alignItems: "center"
            }}
        >
            <Container
                component="main"
                maxWidth="xs"
            >
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        alignItems: "center",
                        boxShadow: 1,
                        p: 2,
                    }}
                >
                    {(tenantLogo || !hideDefaultBranding) && (
                        <Avatar
                            sx={{ width: "140px", height: "100px", m: 1, borderRadius: 0 }}
                            src={tenantLogo || logo}
                        />
                    )}
                    <Typography sx={{ fontWeight: "bold", fontSize: 20, color: 'text.primary' }}>
                        Recover Password
                    </Typography>
                    <Box sx={{ mt: 1, mx: 1, width: "100%", }}>
                        <Divider />
                        <Controller
                            name="email"
                            control={control}
                            rules={{
                                required: "Email is required",
                                pattern: {
                                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                                    message: "Invalid email address",
                                },
                            }}
                            render={({ field }) => (
                                <TextField
                                    sx={{
                                        my: 1.5,
                                    }}
                                    fullWidth
                                    size="small"
                                    label="Email Address"
                                    autoComplete="email"
                                    autoFocus
                                    helperText={errors?.email ? errors?.email?.message : ""}
                                    error={errors?.email}
                                    {...field}
                                />
                            )}
                        />
                        {openDrawer && email.current &&
                            <Dialog open={openDrawer} fullScreen>
                                <Grid
                                    container
                                    sx={{
                                        minHeight: "600px",
                                        justifyContent: "center",
                                        alignItems: "center"
                                    }}
                                >
                                    <Container component="main" maxWidth="xs">
                                        <Box
                                            sx={{
                                                display: "flex",
                                                flexDirection: "column",
                                                alignItems: "center",
                                                boxShadow: 1,
                                                px: 2,
                                            }}
                                        >
                                            {(tenantLogo || !hideDefaultBranding) && (
                                                <Avatar
                                                    sx={{ width: "140px", height: "100px", m: 1, borderRadius: 0 }}
                                                    src={tenantLogo || logo}
                                                />
                                            )}
                                            <Typography sx={{ fontWeight: "bold", fontSize: 20, color: 'text.primary' }}>
                                                Recover Password
                                            </Typography>
                                            <Box component="form" onSubmit={handleSubmit(onReset)} sx={{ my: 2 }}>
                                                <Divider />
                                                <Typography sx={{ fontWeight: 20, my: 1, color: 'text.secondary' }}>
                                                    Please put the short code sent via SMS to the phone linked with the email: <strong style={{ color: mode === 'light' ? 'primary.main' : 'primary.light' }}>{email.current}</strong> and then set your new password.
                                                </Typography>
                                                <Box sx={{ mt: 1 }}>
                                                    <Divider />
                                                    <Controller
                                                        name="reset_code"
                                                        control={control}
                                                        rules={{
                                                            required: "Code can't be empty"
                                                        }}
                                                        render={({ field }) => (
                                                            <TextField
                                                                id="code"
                                                                sx={{
                                                                    mt: 1.5,
                                                                    mb: 1
                                                                }}
                                                                label="Code"
                                                                fullWidth
                                                                type={codeInputType}
                                                                autoComplete="reset_code"
                                                                error={!!errors?.reset_code}
                                                                helperText={errors?.reset_code ? errors.reset_code.message : null}
                                                                size="small"
                                                                disabled={cooldownHook.isBlocked}
                                                                {...field}
                                                                InputProps={{
                                                                    endAdornment: (
                                                                        <InputAdornment position="end">
                                                                            <IconButton
                                                                                size="small"
                                                                                onClick={() => {
                                                                                    setShowCode(!showCode);
                                                                                    setCodeInputType(showCode === false ? "text" : "password");
                                                                                }}
                                                                            >
                                                                                {showCode ? <Visibility sx={{ fontSize: "inherit" }} /> : <VisibilityOff sx={{ fontSize: "inherit" }} />}
                                                                            </IconButton>
                                                                        </InputAdornment>
                                                                    ),
                                                                }}
                                                            />
                                                        )}
                                                    />
                                                    <Controller
                                                        name="password"
                                                        control={control}
                                                        rules={{
                                                            required: "Password is required",
                                                            minLength: {
                                                                value: 6,
                                                                message: "At least 6 characters is required."
                                                            },
                                                            maxLength: {
                                                                value: 32,
                                                                message: "The maximum characters allowed is 32."
                                                            }
                                                        }}
                                                        render={({ field }) => (
                                                            <TextField
                                                                id="new-password-input"
                                                                sx={{
                                                                    mt: 0.5,
                                                                    mb: 1
                                                                }}
                                                                label="New Password"
                                                                fullWidth
                                                                type={inputType}
                                                                autoComplete="new-password"
                                                                error={!!errors?.password}
                                                                helperText={errors?.password ? errors.password.message : null}
                                                                size="small"
                                                                {...field}
                                                                InputProps={{
                                                                    endAdornment: (
                                                                        <InputAdornment position="end">
                                                                            <IconButton
                                                                                size="small"
                                                                                onClick={() => {
                                                                                    setShowPassword(showPassword === false ? true : false);
                                                                                    setInputType(showPassword === false ? "text" : "password");
                                                                                }}
                                                                            >
                                                                                {showPassword ? <Visibility sx={{ fontSize: "inherit" }} /> : <VisibilityOff sx={{ fontSize: "inherit" }} />}
                                                                            </IconButton>
                                                                        </InputAdornment>
                                                                    ),
                                                                }}
                                                            />
                                                        )}
                                                    />
                                                    <Controller
                                                        name="confirm_password"
                                                        control={control}
                                                        rules={{
                                                            validate: value =>
                                                                value === password.current || "The passwords do not match"
                                                        }}
                                                        render={({ field }) => (
                                                            <TextField
                                                                id="confirm-password-input"
                                                                sx={{
                                                                    mt: 0.5,
                                                                    mb: 1.5
                                                                }}
                                                                label="Confirm Password"
                                                                fullWidth
                                                                type={confirmInputType}
                                                                autoComplete="confirm-current-password"
                                                                error={!!errors?.confirm_password}
                                                                helperText={
                                                                    errors?.confirm_password
                                                                        ? errors.confirm_password.message
                                                                        : null
                                                                }
                                                                size="small"
                                                                {...field}
                                                                InputProps={{
                                                                    endAdornment: (
                                                                        <InputAdornment position="end">
                                                                            <IconButton
                                                                                size="small"
                                                                                onClick={() => {
                                                                                    setShowConfirmPassword(showConfirmPassword === false ? true : false);
                                                                                    setConfirmInputType(showConfirmPassword === false ? "text" : "password");
                                                                                }}
                                                                            >
                                                                                {showConfirmPassword ? <Visibility sx={{ fontSize: "inherit" }} /> : <VisibilityOff sx={{ fontSize: "inherit" }} />}
                                                                            </IconButton>
                                                                        </InputAdornment>
                                                                    ),
                                                                }}
                                                            />
                                                        )}
                                                    />
                                                    <Divider />
                                                    <Button
                                                        size="small"
                                                        variant="text"
                                                        type="text"
                                                        sx={{
                                                            ":hover": { bgcolor: 'background.default' },
                                                            backgroundColor: 'background.default',
                                                            color: cooldownHook.isOnCooldown ? "text.disabled" : "primary.main",
                                                            variant: "body2"
                                                        }}
                                                        onClick={() => {
                                                            if (cooldownHook.canResend) {
                                                                setRequestOtp(true)
                                                            }
                                                        }}
                                                        disabled={otpIsFetching || !cooldownHook.canResend}
                                                    >
                                                        {cooldownHook.isOnCooldown ? `Resend OTP (${cooldownHook.formattedTime})` : 'Resend OTP'}
                                                        {otpIsFetching && (
                                                            <CircularProgress
                                                                size={24}
                                                                sx={{
                                                                    color: "secondary.main",
                                                                    position: 'absolute',
                                                                    top: '50%',
                                                                    left: '50%',
                                                                    marginTop: '-12px',
                                                                    marginLeft: '-12px',
                                                                }}
                                                            />
                                                        )}
                                                    </Button>
                                                </Box>
                                                <Divider />
                                                <Box
                                                    sx={{
                                                        my: 1,
                                                        display: 'flex',
                                                        justifyContent: "space-between",
                                                        alignItems: "center"
                                                    }}
                                                >
                                                    <Button
                                                        size="small"
                                                        variant="contained"
                                                        sx={{
                                                            ":hover": { bgcolor: "secondary.dark" },
                                                            backgroundColor: "secondary.main",
                                                            color: "secondary.contrastText",
                                                        }}
                                                        onClick={() => setOpenDrawer(false)}
                                                    >
                                                        Back
                                                    </Button>
                                                    <Button
                                                        size="small"
                                                        variant="contained"
                                                        type="submit"
                                                        sx={{
                                                            ":hover": { bgcolor: "secondary.main" },
                                                            backgroundColor: "primary.main",
                                                            color: "primary.contrastText",
                                                        }}
                                                        onClick={(formData) => onReset(formData)}
                                                        disabled={isLoading}
                                                    >
                                                        Submit
                                                        {isLoading && (
                                                            <CircularProgress
                                                                size={24}
                                                                sx={{
                                                                    color: "primary.paper",
                                                                    position: 'absolute',
                                                                    top: '50%',
                                                                    left: '50%',
                                                                    marginTop: '-12px',
                                                                    marginLeft: '-12px',
                                                                }}
                                                            />
                                                        )}
                                                    </Button>
                                                </Box>
                                            </Box>
                                        </Box>
                                    </Container>
                                </Grid>
                            </Dialog>}
                        <Divider />
                        <Box
                            sx={{
                                my: 1.5,
                                display: 'flex',
                                justifyContent: "space-between",
                                alignItems: "center"
                            }}
                        >
                            <Button
                                size="small"
                                variant="contained"
                                sx={{
                                    ":hover": { bgcolor: "secondary.dark" },
                                    backgroundColor: "secondary.main",
                                    color: "secondary.contrastText",
                                }}
                                onClick={handleClick}
                            >
                                Back
                            </Button>
                            <Button
                                size="small"
                                variant="contained"
                                sx={{
                                    textTransform: "none",
                                    ":hover": { bgcolor: "primary.main" },
                                    color: "primary.contrastText",
                                }}
                                onClick={() => onSubmit()}
                            >
                                Next
                            </Button>
                        </Box>
                    </Box>
                </Box>
            </Container>
        </Grid>
    );
}
export default ForgetPassword